import { K8sProvider } from '@cast/shared-types';
import { Cluster, ClusterStatus } from '@cast/types';

import { useIsClusterDemoMode } from '@cast/infrastructure-demo';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { useCorrectRebalancePlanQuery } from 'features/cluster/rebalance/_hooks/useCorrectRebalancePlanQuery';
import { useCorrectRebalancePlansQuery } from 'features/cluster/rebalance/_hooks/useCorrectRebalancePlansQuery';
import { isNormalizedRebalancingPlanInProgress } from 'features/cluster/rebalance/_utils/normalizedRebalancingPlanStatus';
import { getRebalanceProgress } from 'features/cluster/rebalance/utils';
import { useIsReadOnlyClusterEligibleForRebalancingPlanCreation } from 'features/kent/hooks/useIsReadOnlyClusterEligibleForRebalancingPlanCreation';

import { SidebarItemProps } from '../../_components/SidebarItem';

import {
  isClusterManaged,
  isKentManagedCluster,
} from '@cast/shared-cluster-context-data-access';

export const useRebalancingPlanSidebarItemProps = (
  cluster: Cluster
): Omit<SidebarItemProps, 'title'> => {
  const basePath = `${PRODUCT_BASE_PATHS.automation}/external-clusters/${cluster.id}`;
  const rebalancerPath = `${basePath}/rebalance`;
  const clusterId = cluster.id;
  const isManaged = isClusterManaged(cluster);
  const isClusterDemoMode = useIsClusterDemoMode();

  const isKentManaged = isKentManagedCluster(cluster);
  const isReadOnlyClusterEligibleForRebalancingPlanCreation =
    useIsReadOnlyClusterEligibleForRebalancingPlanCreation(cluster);

  const { isLoading: isLoadingRebalancingPlans, normalizedRebalancingPlans } =
    useCorrectRebalancePlansQuery({
      clusterId,
      limit: 1,
      disabled: !isManaged && !isKentManaged,
      statusFilter: 'in_progress',
    });
  const activeRebalancingPlanId =
    normalizedRebalancingPlans?.[0]?.rebalancingPlanId ?? undefined;

  const { normalizedRebalancingPlan } = useCorrectRebalancePlanQuery({
    clusterId,
    rebalancePlanId: activeRebalancingPlanId,
    includeOperations: true,
  });

  if (!isClusterDemoMode) {
    if (cluster?.providerType === K8sProvider.ANYWHERE) {
      return {
        url: rebalancerPath,
        disabled: true,
      };
    }
    if (isLoadingRebalancingPlans) {
      return {
        url: rebalancerPath,
        disabled: true,
        tooltip: `Page is not ready yet, loading rebalancing history please wait...`,
      };
    }
    if (
      !isManaged &&
      !isKentManaged &&
      !isReadOnlyClusterEligibleForRebalancingPlanCreation
    ) {
      return {
        url: rebalancerPath,
        disabled: true,
        tooltip: 'Enable cost optimization',
      };
    }
    if (
      cluster &&
      ![ClusterStatus.READY, ClusterStatus.WARNING].includes(cluster.status) &&
      !isReadOnlyClusterEligibleForRebalancingPlanCreation
    ) {
      return {
        url: rebalancerPath,
        disabled: true,
        tooltip: 'Invalid cluster status, cluster must be in ready state',
      };
    }
  }

  if (
    normalizedRebalancingPlan &&
    isNormalizedRebalancingPlanInProgress(normalizedRebalancingPlan)
  ) {
    const progress = getRebalanceProgress(normalizedRebalancingPlan);

    return {
      url: rebalancerPath,
      progressBar: {
        show: Boolean(progress.overall?.percentage),
        value: progress.overall?.percentage ?? 0,
      },
      disabled: false,
    };
  }

  return {
    url: rebalancerPath,
    disabled: false,
  };
};
